/**
 * AI Prompt Editor Modal
 * 
 * Admin interface for editing and versioning AI prompts for school summaries.
 * Provides tabs for each prompt type with version history.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Tabs,
  Tab,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Code as CodeIcon,
  Info as InfoIcon,
  HelpOutline as HelpIcon,
  Edit as EditIcon,
  SwapHoriz as SwapIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { listPrompts, createPrompt, activatePrompt } from '../../../services/aiPromptService';

export const SINGLE_SCHOOL_PROMPT_TYPES = [
  'geographic', 'geographic_drop_a_pin', 'competitive', 'projections', 'combined'
];

export const MULTI_SCHOOL_PROMPT_TYPES = [
  'multi_school_overview', 'multi_school_geographic',
  'multi_school_competitive', 'multi_school_projections'
];

const PromptEditor = ({
  open = true,
  onClose,
  variant = 'dialog',
  types
}) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // State for tabs
  const [activeTab, setActiveTab] = useState(0);
  
  // State for prompts by type
  const [allPrompts, setAllPrompts] = useState({
    geographic: [],
    geographic_drop_a_pin: [],
    competitive: [],
    projections: [],
    combined: [],
    multi_school_overview: [],
    multi_school_geographic: [],
    multi_school_competitive: [],
    multi_school_projections: []
  });
  
  // State for current editing
  const [editingPrompts, setEditingPrompts] = useState({
    geographic: { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' },
    geographic_drop_a_pin: { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' },
    competitive: { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' },
    projections: { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' },
    combined: { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' },
    multi_school_overview: { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' },
    multi_school_geographic: { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' },
    multi_school_competitive: { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' },
    multi_school_projections: { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' }
  });
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Error state
  const [error, setError] = useState(null);
  
  // Editor mode (simple vs advanced)
  const [editorMode, setEditorMode] = useState({
    geographic: 'simple',
    geographic_drop_a_pin: 'simple',
    competitive: 'simple',
    projections: 'simple',
    combined: 'simple',
    multi_school_overview: 'advanced',
    multi_school_geographic: 'advanced',
    multi_school_competitive: 'advanced',
    multi_school_projections: 'advanced'
  });
  
  // Simple mode fields (parsed from JSON)
  const [simpleFields, setSimpleFields] = useState({
    geographic: { 
      introduction: '', 
      dataPoints: '', 
      executiveSummary: '',
      executiveSummaryExample: '',
      sections: [
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' }
      ],
      requirements: '' 
    },
    geographic_drop_a_pin: { 
      introduction: '', 
      dataPoints: '', 
      executiveSummary: '',
      executiveSummaryExample: '',
      sections: [
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' }
      ],
      requirements: '' 
    },
    competitive: { 
      introduction: '', 
      dataPoints: '', 
      executiveSummary: '',
      executiveSummaryExample: '',
      sections: [
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' }
      ],
      requirements: '' 
    },
    projections: { 
      introduction: '', 
      dataPoints: '', 
      executiveSummary: '',
      executiveSummaryExample: '',
      sections: [
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' }
      ],
      requirements: '' 
    },
    combined: { 
      introduction: '', 
      dataPoints: '', 
      executiveSummary: '',
      executiveSummaryExample: '',
      sections: [
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' },
        { title: '', description: '', example: '' }
      ],
      requirements: '' 
    }
  });
  
  const promptTypes = types || (variant === 'inline' ? MULTI_SCHOOL_PROMPT_TYPES : SINGLE_SCHOOL_PROMPT_TYPES);
  const promptTypeLabels = {
    geographic: 'Geographic',
    geographic_drop_a_pin: 'Drop-A-Pin Geographic',
    competitive: 'Competitive',
    projections: 'Projections',
    combined: 'Overview',
    multi_school_overview: 'Multi-School Overview',
    multi_school_geographic: 'Multi-School Geographic',
    multi_school_competitive: 'Multi-School Competitive',
    multi_school_projections: 'Multi-School Projections'
  };
  
  // Available placeholders for each prompt type
  const availablePlaceholders = {
    geographic: [
      { name: '{school_name}', desc: 'Name of the school' },
      { name: '{age_range_text}', desc: 'Age range (e.g., "5-17")' },
      { name: '{population_2020}', desc: 'Population in 2020 (Census)' },
      { name: '{population_current}', desc: 'Current population (2025 Esri)' },
      { name: '{population_2030}', desc: 'Projected 2030 population (Esri)' },
      { name: '{change_past_pct}', desc: 'Area population change % (2020-2025)' },
      { name: '{change_future_pct}', desc: 'Area population change % (2025-2030 projected)' },
      { name: '{trend}', desc: 'Population trend (growing/declining/stable)' },
      { name: '{total_enrollment}', desc: 'School enrollment' },
      { name: '{governance}', desc: 'School governance type' },
      { name: '{nearby_schools_formatted}', desc: 'Nearby schools (100+ enrollment, overlapping grades, excludes virtual/alternative)' },
      { name: '{new_expanding_schools_formatted}', desc: 'New/expanding schools list' },
      { name: '{county_charter_count}', desc: 'Number of county charter schools with overlapping grades' },
      { name: '{county_name}', desc: 'Name of the county' },
      { name: '{county_charter_median_enrollment_change}', desc: 'Median enrollment change % for county charters (2019-2020 to 2024-2025)' },
      { name: '{verbosity_instructions}', desc: 'Output length instructions' }
    ],
    geographic_drop_a_pin: [
      { name: '{school_name}', desc: 'Name of the location' },
      { name: '{age_range_text}', desc: 'Age range (e.g., "5-17")' },
      { name: '{population_2020}', desc: 'Population in 2020 (Census)' },
      { name: '{population_current}', desc: 'Current population (2025 Esri)' },
      { name: '{population_2030}', desc: 'Projected 2030 population (Esri)' },
      { name: '{change_past_pct}', desc: 'Area population change % (2020-2025)' },
      { name: '{change_future_pct}', desc: 'Area population change % (2025-2030 projected)' },
      { name: '{trend}', desc: 'Population trend (growing/declining/stable)' },
      { name: '{nearby_schools_by_level_formatted}', desc: 'Nearby schools grouped by level (Elementary/Middle/High) with distance and enrollment trends' },
      { name: '{declining_enrollment_pct}', desc: '% of nearby schools with declining enrollment (2019-2020 to 2024-2025)' },
      { name: '{grade_range_elementary}', desc: 'Elementary grade range (K-5)' },
      { name: '{grade_range_middle}', desc: 'Middle grade range (6-8)' },
      { name: '{grade_range_high}', desc: 'High grade range (9-12)' },
      { name: '{new_expanding_schools_formatted}', desc: 'New/expanding schools list' },
      { name: '{county_charter_count}', desc: 'Number of county charter schools' },
      { name: '{county_name}', desc: 'Name of the county' },
      { name: '{county_charter_median_enrollment_change}', desc: 'Median enrollment change % for county charters (2019-2020 to 2024-2025)' },
      { name: '{verbosity_instructions}', desc: 'Output length instructions' }
    ],
    competitive: [
      { name: '{school_name}', desc: 'Name of the school' },
      { name: '{total_enrollment}', desc: 'School enrollment' },
      { name: '{governance}', desc: 'School governance type' },
      { name: '{market_share_past}', desc: 'Past market share %' },
      { name: '{market_share_current}', desc: 'Current market share %' },
      { name: '{market_share_change}', desc: 'Market share change %' },
      { name: '{expected_market_share}', desc: 'Expected market share % (excludes private schools)' },
      { name: '{status}', desc: 'Market share status' },
      { name: '{focus_schools_formatted}', desc: 'List of focus schools with FRL % (excludes virtual/alternative)' },
      { name: '{demographics_formatted}', desc: 'Demographics comparison (includes FRL)' },
      { name: '{academic_data_formatted}', desc: 'Academic performance data (state test scores, letter grades)' },
      { name: '{new_expanding_schools_formatted}', desc: 'New/expanding schools list' },
      { name: '{population_change_formatted}', desc: 'Area population change 2020-2025' },
      { name: '{school_info_formatted}', desc: 'School model/program info from web search' },
      { name: '{verbosity_instructions}', desc: 'Output length instructions' }
    ],
    projections: [
      { name: '{school_name}', desc: 'Name of the school' },
      { name: '{total_enrollment}', desc: 'School enrollment' },
      { name: '{governance}', desc: 'School governance type' },
      { name: '{historical_formatted}', desc: 'Historical enrollment data (user-entered when available, otherwise NCES)' },
      { name: '{projections_formatted}', desc: 'Projection data (5-year forecast)' },
      { name: '{population_projection}', desc: 'Area population projection text (2025-2030, age-filtered)' },
      { name: '{change_future_pct}', desc: 'Area population change % (2025-2030)' },
      { name: '{data_source}', desc: 'Enrollment data source (user or nces)' },
      { name: '{verbosity_instructions}', desc: 'Output length instructions' }
    ],
    combined: [
      { name: '{school_name}', desc: 'Name of the school' },
      { name: '{total_enrollment}', desc: 'School enrollment' },
      { name: '{governance}', desc: 'School governance type' },
      { name: '{geographic_summary}', desc: 'Executive summary from Geographic analysis' },
      { name: '{competitive_summary}', desc: 'Executive summary from Competitive analysis' },
      { name: '{projections_summary}', desc: 'Executive summary from Projections analysis' },
      { name: '{verbosity_instructions}', desc: 'Output length instructions' }
    ],
    multi_school_geographic: [
      { name: '{school_count}', desc: 'Number of schools in the selected set' },
      { name: '{school_list}', desc: 'Comma-separated list of selected school names' },
      { name: '{drive_time}', desc: 'Drive time in minutes (5, 10, or 15)' },
      { name: '{travel_mode_label}', desc: '"driving" or "walking"' },
      { name: '{per_school_summaries}', desc: "Each selected school's stored geographic summary, labeled by school name" },
      { name: '{verbosity_instructions}', desc: 'Output length instructions' }
    ],
    multi_school_competitive: [
      { name: '{school_count}', desc: 'Number of schools in the selected set' },
      { name: '{school_list}', desc: 'Comma-separated list of selected school names' },
      { name: '{drive_time}', desc: 'Drive time in minutes (5, 10, or 15)' },
      { name: '{travel_mode_label}', desc: '"driving" or "walking"' },
      { name: '{per_school_summaries}', desc: "Each selected school's stored competitive summary, labeled by school name" },
      { name: '{verbosity_instructions}', desc: 'Output length instructions' }
    ],
    multi_school_projections: [
      { name: '{school_count}', desc: 'Number of schools in the selected set' },
      { name: '{school_list}', desc: 'Comma-separated list of selected school names' },
      { name: '{drive_time}', desc: 'Drive time in minutes (5, 10, or 15)' },
      { name: '{travel_mode_label}', desc: '"driving" or "walking"' },
      { name: '{per_school_summaries}', desc: "Each selected school's stored projections summary, labeled by school name" },
      { name: '{verbosity_instructions}', desc: 'Output length instructions' }
    ],
    multi_school_overview: [
      { name: '{school_count}', desc: 'Number of schools in the selected set' },
      { name: '{school_list}', desc: 'Comma-separated list of selected school names' },
      { name: '{drive_time}', desc: 'Drive time in minutes (5, 10, or 15)' },
      { name: '{travel_mode_label}', desc: '"driving" or "walking"' },
      { name: '{multi_geographic_exec}', desc: 'Executive summary from the Multi-School Geographic rollup' },
      { name: '{multi_competitive_exec}', desc: 'Executive summary from the Multi-School Competitive rollup' },
      { name: '{multi_projections_exec}', desc: 'Executive summary from the Multi-School Projections rollup' },
      { name: '{verbosity_instructions}', desc: 'Output length instructions' }
    ]
  };
  
  const loadPrompts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listPrompts();
      setAllPrompts(response.prompts);
      
      // Set editing state to active versions
      const newEditingPrompts = {};
      const newSimpleFields = {};
      promptTypes.forEach(type => {
        const promptsForType = response.prompts[type] || []; // Safety check for new prompt types
        const activePrompt = promptsForType.find(p => p.is_active);
        if (activePrompt) {
          newEditingPrompts[type] = {
            systemPrompt: activePrompt.system_prompt,
            userPromptTemplate: activePrompt.user_prompt_template,
            exampleOutput: activePrompt.example_output || '',
            notes: ''
          };
          // Parse for simple mode
          const parsedTemplate = parsePromptToSimpleFields(activePrompt.user_prompt_template);
          const { executiveSummaryExample, sectionsWithExamples } = parseExampleOutput(
            activePrompt.example_output,
            parsedTemplate.sections
          );
          newSimpleFields[type] = {
            ...parsedTemplate,
            executiveSummaryExample,
            sections: sectionsWithExamples
          };
        } else {
          newEditingPrompts[type] = { systemPrompt: '', userPromptTemplate: '', exampleOutput: '', notes: '' };
          newSimpleFields[type] = { introduction: '', dataPoints: '', executiveSummary: '', executiveSummaryExample: '', sections: [], requirements: '' };
        }
      });
      setEditingPrompts(newEditingPrompts);
      setSimpleFields(newSimpleFields);
    } catch (err) {
      console.error('Error loading prompts:', err);
      setError(err.message);
      enqueueSnackbar(`Failed to load prompts: ${err.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Load prompts when the dialog opens, or immediately in inline mode
  useEffect(() => {
    if (variant === 'inline' || open) {
      loadPrompts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variant]);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const parsePromptToSimpleFields = (template) => {
    // Extract sections from the template
    const lines = template.split('\n');
    let introduction = '';
    let dataPoints = '';
    let executiveSummary = '';
    const sections = [];
    let requirements = '';
    
    let currentSection = 'introduction';
    let inJSON = false;
    let currentJSONSection = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.includes('**OUTPUT FORMAT') || line.includes('OUTPUT FORMAT')) {
        currentSection = 'outputFormat';
        inJSON = true;
      } else if (line.includes('**Requirements:') || line.includes('Requirements:')) {
        currentSection = 'requirements';
        inJSON = false;
      } else if (currentSection === 'introduction' && line.startsWith('**') && line.includes(':')) {
        currentSection = 'dataPoints';
      }
      
      // Parse executive summary from JSON
      if (inJSON && line.includes('"executive_summary"')) {
        const match = line.match(/"executive_summary":\s*"([^"]+)"/);
        if (match) executiveSummary = match[1];
      }
      
      // Parse sections from JSON - handle both regular and table types
      if (inJSON && line.includes('"title"')) {
        const titleMatch = line.match(/"title":\s*"([^"]+)"/);
        if (titleMatch) {
          const title = titleMatch[1];
          currentJSONSection = { title, description: '', tableStructure: null };
          
          // Look for type and content/competitors on next lines
          for (let j = i + 1; j < lines.length && !lines[j].includes('"title"') && !lines[j].includes('}}'); j++) {
            // Check if this is a table section
            const typeMatch = lines[j].match(/"type":\s*"([^"]+)"/);
            if (typeMatch && typeMatch[1] === 'table') {
              // This is a table section - extract competitor structure
              const competitorsStart = j;
              let competitorsEnd = competitorsStart;
              let braceCount = 0;
              let inCompetitors = false;
              
              for (let k = competitorsStart; k < lines.length; k++) {
                if (lines[k].includes('"competitors"')) {
                  inCompetitors = true;
                }
                if (inCompetitors) {
                  if (lines[k].includes('[')) braceCount++;
                  if (lines[k].includes(']')) {
                    braceCount--;
                    if (braceCount === 0) {
                      competitorsEnd = k;
                      break;
                    }
                  }
                }
              }
              
              // Extract the table structure
              const tableLines = lines.slice(competitorsStart, competitorsEnd + 1);
              const tableFields = [];
              for (const tLine of tableLines) {
                const fieldMatch = tLine.match(/"(\w+)":\s*"([^"]+)"/);
                if (fieldMatch && fieldMatch[1] !== 'type') {
                  tableFields.push({ name: fieldMatch[1], description: fieldMatch[2] });
                }
              }
              
              currentJSONSection.tableStructure = {
                type: 'table',
                fields: tableFields
              };
              break;
            }
            
            // Regular content field
            const contentMatch = lines[j].match(/"content":\s*"([^"]+)"/);
            if (contentMatch) {
              currentJSONSection.description = contentMatch[1];
              break;
            }
          }
          
          sections.push(currentJSONSection);
        }
      }
      
      if (currentSection === 'introduction' && !line.includes('OUTPUT FORMAT')) {
        introduction += line + '\n';
      } else if (currentSection === 'dataPoints' && !line.includes('OUTPUT FORMAT') && !line.includes('Requirements:')) {
        dataPoints += line + '\n';
      } else if (currentSection === 'requirements') {
        requirements += line + '\n';
      }
    }
    
    return {
      introduction: introduction.trim(),
      dataPoints: dataPoints.trim(),
      executiveSummary: executiveSummary || 'Brief 1-2 sentence headline capturing the key finding',
      executiveSummaryExample: '', // Will be parsed from example_output separately
      sections: sections.map(s => ({ ...s, example: '' })), // Add empty example field
      requirements: requirements.trim()
    };
  };
  
  const parseExampleOutput = (exampleOutput, sections) => {
    if (!exampleOutput || !exampleOutput.trim()) {
      return { executiveSummaryExample: '', sectionsWithExamples: sections.map(s => ({ ...s, example: '' })) };
    }
    
    try {
      const parsed = JSON.parse(exampleOutput);
      const executiveSummaryExample = parsed.executive_summary || '';
      
      // Map section examples to sections by title
      const sectionsWithExamples = sections.map(section => {
        const matchingExample = parsed.sections?.find(s => s.title === section.title);
        return {
          ...section,
          example: matchingExample?.content || ''
        };
      });
      
      return { executiveSummaryExample, sectionsWithExamples };
    } catch (e) {
      // If parsing fails, return empty examples
      console.warn('Failed to parse example output:', e);
      return { executiveSummaryExample: '', sectionsWithExamples: sections.map(s => ({ ...s, example: '' })) };
    }
  };
  
  const buildPromptFromSimpleFields = (fields) => {
    // Build the output format JSON structure
    const sectionsJSON = fields.sections.map(s => {
      // If this is a table section, preserve the table structure
      if (s.tableStructure) {
        const fieldsJSON = s.tableStructure.fields.map(field => 
          `          "${field.name}": "${field.description}"`
        ).join(',\n');
        
        return `    {
      "title": "${s.title}",
      "type": "table",
      "competitors": [
        {
${fieldsJSON}
        }
      ]
    }`;
      }
      
      // Regular content section
      return `    {
      "title": "${s.title}",
      "content": "${s.description}"
    }`;
    }).join(',\n');
    
    const outputFormat = `**OUTPUT FORMAT - CRITICAL:**
You MUST return your analysis as a JSON object with the following structure. Each section will be displayed in a separate colored card in the UI.

{
  "executive_summary": "${fields.executiveSummary}",
  "sections": [
${sectionsJSON}
  ]
}`;
    
    // Reconstruct the full template
    return `${fields.introduction}\n\n${fields.dataPoints}\n\n${outputFormat}\n\n${fields.requirements}`;
  };
  
  const buildExampleOutputFromSimpleFields = (fields) => {
    // Build example output JSON from individual section examples
    const hasExecutiveSummaryExample = fields.executiveSummaryExample && fields.executiveSummaryExample.trim();
    const hasSectionExamples = fields.sections.some(s => s.example && s.example.trim());
    
    if (!hasExecutiveSummaryExample && !hasSectionExamples) {
      return ''; // No examples provided
    }
    
    const sectionsExamplesJSON = fields.sections
      .filter(s => s.title && s.title.trim()) // Only include sections with titles
      .map(s => {
        const exampleContent = s.example && s.example.trim() 
          ? s.example.trim() 
          : `[Example text for ${s.title}]`;
        return `    {
      "title": "${s.title}",
      "content": "${exampleContent.replace(/"/g, '\\"').replace(/\n/g, '\\n')}"
    }`;
      }).join(',\n');
    
    const executiveSummaryExample = hasExecutiveSummaryExample
      ? fields.executiveSummaryExample.trim().replace(/"/g, '\\"').replace(/\n/g, '\\n')
      : '[Example executive summary]';
    
    return `{
  "executive_summary": "${executiveSummaryExample}",
  "sections": [
${sectionsExamplesJSON}
  ]
}`;
  };
  
  const handlePromptChange = (type, field, value) => {
    setEditingPrompts(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };
  
  const handleSimpleFieldChange = (type, field, value) => {
    const newFields = {
      ...simpleFields[type],
      [field]: value
    };
    setSimpleFields(prev => ({
      ...prev,
      [type]: newFields
    }));
    
    // Auto-sync to advanced mode
    const rebuiltTemplate = buildPromptFromSimpleFields(newFields);
    const rebuiltExample = buildExampleOutputFromSimpleFields(newFields);
    setEditingPrompts(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        userPromptTemplate: rebuiltTemplate,
        exampleOutput: rebuiltExample
      }
    }));
  };
  
  const handleSectionChange = (type, sectionIndex, field, value) => {
    const newSections = [...simpleFields[type].sections];
    newSections[sectionIndex] = {
      ...newSections[sectionIndex],
      [field]: value
    };
    
    const newFields = {
      ...simpleFields[type],
      sections: newSections
    };
    
    setSimpleFields(prev => ({
      ...prev,
      [type]: newFields
    }));
    
    // Auto-sync to advanced mode
    const rebuiltTemplate = buildPromptFromSimpleFields(newFields);
    const rebuiltExample = buildExampleOutputFromSimpleFields(newFields);
    setEditingPrompts(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        userPromptTemplate: rebuiltTemplate,
        exampleOutput: rebuiltExample
      }
    }));
  };
  
  const toggleEditorMode = (type) => {
    const newMode = editorMode[type] === 'simple' ? 'advanced' : 'simple';
    
    if (newMode === 'simple') {
      // Switching to simple: parse current template and example output
      const parsedTemplate = parsePromptToSimpleFields(editingPrompts[type].userPromptTemplate);
      const { executiveSummaryExample, sectionsWithExamples } = parseExampleOutput(
        editingPrompts[type].exampleOutput,
        parsedTemplate.sections
      );
      setSimpleFields(prev => ({
        ...prev,
        [type]: {
          ...parsedTemplate,
          executiveSummaryExample,
          sections: sectionsWithExamples
        }
      }));
    }
    
    setEditorMode(prev => ({
      ...prev,
      [type]: newMode
    }));
  };
  
  const handleSavePrompt = async (type) => {
    const { systemPrompt, userPromptTemplate, exampleOutput, notes } = editingPrompts[type];
    
    if (!systemPrompt.trim()) {
      enqueueSnackbar('System prompt cannot be empty', { variant: 'error' });
      return;
    }
    
    if (!userPromptTemplate.trim()) {
      enqueueSnackbar('User prompt template cannot be empty', { variant: 'error' });
      return;
    }
    
    setSaving(true);
    try {
      const newPrompt = await createPrompt(type, systemPrompt, userPromptTemplate, exampleOutput, notes);
      enqueueSnackbar(
        `Created ${promptTypeLabels[type]} prompt v${newPrompt.version_number} (now active)`, 
        { variant: 'success' }
      );
      
      // Reload prompts
      await loadPrompts();
      
      // Clear notes field
      setEditingPrompts(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          notes: ''
        }
      }));
    } catch (err) {
      console.error('Error saving prompt:', err);
      enqueueSnackbar(`Failed to save prompt: ${err.message}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };
  
  const handleActivatePrompt = async (promptId, type) => {
    try {
      await activatePrompt(promptId);
      enqueueSnackbar(`Activated ${promptTypeLabels[type]} prompt version`, { variant: 'success' });
      
      // Reload prompts
      await loadPrompts();
    } catch (err) {
      console.error('Error activating prompt:', err);
      enqueueSnackbar(`Failed to activate prompt: ${err.message}`, { variant: 'error' });
    }
  };
  
  const renderPromptEditor = (type) => {
    const { systemPrompt, userPromptTemplate, notes } = editingPrompts[type];
    const versions = allPrompts[type] || [];
    const activeVersion = versions.find(v => v.is_active);
    const placeholders = availablePlaceholders[type] || [];
    
    return (
      <Box sx={{ py: 2 }}>
        {activeVersion && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Currently using version {activeVersion.version_number} 
            {activeVersion.notes && ` - ${activeVersion.notes}`}
          </Alert>
        )}
        
        {/* Help Section */}
        <Accordion sx={{ mb: 3, bgcolor: 'info.lighter' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpIcon color="info" />
              <Typography variant="subtitle1" fontWeight="bold">
                Available Placeholders & Tips
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Use these placeholders in your prompt template. They will be automatically replaced with actual data when generating summaries:
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 1 }}>
              {placeholders.map((ph) => (
                <Box key={ph.name} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Chip 
                    label={ph.name} 
                    size="small" 
                    sx={{ fontFamily: 'monospace', fontSize: '0.75em' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {ph.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Tips:</strong>
              <br />• Keep JSON structure intact - only edit the text inside quotes
              <br />• Use double curly braces for JSON: {'{{'} and {'}}'}
              <br />• Single curly braces are for placeholders: {'{school_name}'}
              <br />• Test changes with a school before deploying widely
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        {/* System Prompt */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
          System Prompt
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The context/role given to the AI (e.g., "You are an education data analyst...")
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={systemPrompt}
          onChange={(e) => handlePromptChange(type, 'systemPrompt', e.target.value)}
          placeholder="Enter system prompt..."
          variant="outlined"
          sx={{ mb: 3 }}
        />
        
        {/* User Prompt Template with Mode Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            User Prompt Template
            <Chip 
              icon={<InfoIcon />}
              label={`${placeholders.length} placeholders available`}
              size="small"
              color="info"
              variant="outlined"
            />
          </Typography>
          {!type.startsWith('multi_school') && (
            <Button
              size="small"
              startIcon={<SwapIcon />}
              onClick={() => toggleEditorMode(type)}
              variant="outlined"
            >
              {editorMode[type] === 'simple' ? 'Switch to Advanced' : 'Switch to Simple'}
            </Button>
          )}
        </Box>
        
        {editorMode[type] === 'simple' ? (
          // Simple Mode - Section-based editing
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Simple Mode:</strong> Edit each section separately. The full prompt will be built automatically.
            </Alert>
            
            {/* Introduction Section */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon fontSize="small" /> 1. Introduction
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Start your prompt. Set the context and what you're analyzing.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={simpleFields[type]?.introduction || ''}
                onChange={(e) => handleSimpleFieldChange(type, 'introduction', e.target.value)}
                placeholder="e.g., You are analyzing the geographic and demographic context for {school_name}..."
                variant="outlined"
              />
            </Paper>
            
            {/* Data Points Section */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon fontSize="small" /> 2. Data Points
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                List the data you're providing. Use {'{placeholders}'} to insert dynamic values.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={simpleFields[type]?.dataPoints || ''}
                onChange={(e) => handleSimpleFieldChange(type, 'dataPoints', e.target.value)}
                placeholder="e.g., **Population Data:**\n- Current: {population_current}\n- Trend: {trend}"
                variant="outlined"
              />
            </Paper>
            
            {/* Output Format Section - Abstracted */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon fontSize="small" /> 3. Output Format
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Define what the AI should return. The JSON structure will be built automatically.
              </Typography>
              
              {/* Executive Summary */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Executive Summary Instructions:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={simpleFields[type]?.executiveSummary || ''}
                  onChange={(e) => handleSimpleFieldChange(type, 'executiveSummary', e.target.value)}
                  placeholder="e.g., Brief 1-2 sentence headline capturing the key finding"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  label="Example Text"
                  value={simpleFields[type]?.executiveSummaryExample || ''}
                  onChange={(e) => handleSimpleFieldChange(type, 'executiveSummaryExample', e.target.value)}
                  placeholder="e.g., DSST Montview serves an area with a shrinking age 11-13 population..."
                  variant="outlined"
                  helperText="Provide an example of what the executive summary should look like"
                />
              </Box>
              
              {/* Sections */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Analysis Sections:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const newSections = [...(simpleFields[type]?.sections || []), { title: '', description: '', example: '' }];
                      const newFields = { ...simpleFields[type], sections: newSections };
                      setSimpleFields(prev => ({ ...prev, [type]: newFields }));
                      const rebuiltTemplate = buildPromptFromSimpleFields(newFields);
                      const rebuiltExample = buildExampleOutputFromSimpleFields(newFields);
                      setEditingPrompts(prev => ({
                        ...prev,
                        [type]: { ...prev[type], userPromptTemplate: rebuiltTemplate, exampleOutput: rebuiltExample }
                      }));
                    }}
                  >
                    + Add Section
                  </Button>
                  {simpleFields[type]?.sections?.length > 1 && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        const newSections = simpleFields[type].sections.slice(0, -1);
                        const newFields = { ...simpleFields[type], sections: newSections };
                        setSimpleFields(prev => ({ ...prev, [type]: newFields }));
                        const rebuiltTemplate = buildPromptFromSimpleFields(newFields);
                        const rebuiltExample = buildExampleOutputFromSimpleFields(newFields);
                        setEditingPrompts(prev => ({
                          ...prev,
                          [type]: { ...prev[type], userPromptTemplate: rebuiltTemplate, exampleOutput: rebuiltExample }
                        }));
                      }}
                    >
                      Remove Last
                    </Button>
                  )}
                </Box>
              </Box>
              {simpleFields[type]?.sections?.map((section, idx) => (
                <Box key={idx} sx={{ mb: 2, pl: 2, borderLeft: 3, borderColor: 'primary.main' }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Section {idx + 1}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Title"
                    value={section.title}
                    onChange={(e) => handleSectionChange(type, idx, 'title', e.target.value)}
                    placeholder="e.g., Population Trends"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                  
                  {/* Show table structure if this is a table section */}
                  {section.tableStructure ? (
                    <Box>
                      <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                        <Typography variant="caption" fontWeight="bold">
                          Table Section - This section will display as a table with the following columns:
                        </Typography>
                      </Alert>
                      <Box sx={{ pl: 2, mb: 2, bgcolor: 'grey.100', p: 1.5, borderRadius: 1 }}>
                        {section.tableStructure.fields.map((field, fieldIdx) => (
                          <Box key={fieldIdx} sx={{ mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold" sx={{ color: 'primary.main' }}>
                              {field.name}:
                            </Typography>
                            <Typography variant="caption" sx={{ ml: 1, display: 'block', color: 'text.secondary' }}>
                              {field.description}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="caption">
                          To edit table column definitions, use Advanced mode or edit the prompt directly in the database.
                        </Typography>
                      </Alert>
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      label="What should this section contain?"
                      value={section.description}
                      onChange={(e) => handleSectionChange(type, idx, 'description', e.target.value)}
                      placeholder="e.g., Detailed narrative about population changes. Use **bold** for numbers."
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  )}
                  
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    label="Example Text"
                    value={section.example || ''}
                    onChange={(e) => handleSectionChange(type, idx, 'example', e.target.value)}
                    placeholder="e.g., Key numbers: 2020: 7,339 (age 14-17); Current: 7,111; 2030 proj.: 6,115..."
                    variant="outlined"
                    helperText="Provide an example of what this section should look like"
                  />
                </Box>
              ))}
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  All sections will automatically be formatted as JSON with markdown support.
                  Use **bold** and bullet points (-) in your descriptions to guide the AI's formatting.
                </Typography>
              </Alert>
            </Paper>
            
            {/* Requirements Section */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon fontSize="small" /> 4. Requirements
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                List any specific requirements or constraints for the AI.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={simpleFields[type]?.requirements || ''}
                onChange={(e) => handleSimpleFieldChange(type, 'requirements', e.target.value)}
                placeholder="**Requirements:**\n- Be data-driven\n- Use specific numbers"
                variant="outlined"
              />
            </Paper>
          </Box>
        ) : (
          // Advanced Mode - Raw template editing
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Advanced Mode:</strong> You're editing the raw prompt template. Keep the structure intact.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The main prompt with placeholders like {'{school_name}'}, {'{total_enrollment}'}, etc.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={16}
              value={userPromptTemplate}
              onChange={(e) => handlePromptChange(type, 'userPromptTemplate', e.target.value)}
              placeholder="Enter user prompt template with {placeholders}..."
              variant="outlined"
              sx={{ 
                mb: 1,
                '& .MuiInputBase-input': {
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: '0.85em',
                  lineHeight: 1.6
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Chip 
                label={`${userPromptTemplate.length} characters`}
                size="small"
                variant="outlined"
              />
              <Chip 
                label={`${userPromptTemplate.split('\n').length} lines`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
        
        {/* Notes */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Version Notes (Optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Describe what changed in this version
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          value={notes}
          onChange={(e) => handlePromptChange(type, 'notes', e.target.value)}
          placeholder="e.g., Made output more concise, added market share emphasis"
          variant="outlined"
          sx={{ mb: 3 }}
        />
        
        {/* Save Button */}
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={() => handleSavePrompt(type)}
          disabled={saving}
          sx={{ mb: 4 }}
        >
          {saving ? 'Saving...' : 'Save as New Version'}
        </Button>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Version History */}
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon /> Version History
        </Typography>
        
        {versions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No versions yet. Save your first version above.
          </Typography>
        ) : (
          versions.map((version) => (
            <Accordion key={version.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Chip 
                    label={`v${version.version_number}`} 
                    color={version.is_active ? 'primary' : 'default'}
                    size="small"
                  />
                  {version.is_active && (
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="Active" 
                      color="success" 
                      size="small"
                    />
                  )}
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {version.notes || 'No notes'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(version.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>System Prompt:</Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'grey.100', 
                    borderRadius: 1, 
                    mb: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.85em',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {version.system_prompt}
                  </Box>
                  
                  <Typography variant="subtitle2" gutterBottom>User Prompt Template:</Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'grey.100', 
                    borderRadius: 1, 
                    mb: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.85em',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}>
                    {version.user_prompt_template}
                  </Box>
                  
                  {version.example_output && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Example Output:
                        <Chip label="Few-Shot Learning" size="small" color="success" variant="outlined" />
                      </Typography>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'success.lighter', 
                        borderRadius: 1, 
                        mb: 2,
                        fontFamily: 'monospace',
                        fontSize: '0.85em',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '400px',
                        overflow: 'auto',
                        border: '2px solid',
                        borderColor: 'success.main'
                      }}>
                        {version.example_output}
                      </Box>
                    </>
                  )}
                  
                  {!version.is_active && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleActivatePrompt(version.id, type)}
                    >
                      Switch to This Version
                    </Button>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
    );
  };
  
  const editorBody = loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  ) : error ? (
    <Alert severity="error" sx={{ m: 2 }}>
      {error}
    </Alert>
  ) : (
    <>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {promptTypes.map((type) => (
          <Tab
            key={type}
            label={promptTypeLabels[type]}
          />
        ))}
      </Tabs>

      <Box sx={{
        px: variant === 'inline' ? 0 : 3,
        overflowY: 'auto',
        maxHeight: variant === 'inline' ? 'none' : 'calc(90vh - 200px)'
      }}>
        {renderPromptEditor(promptTypes[activeTab])}
      </Box>
    </>
  );

  if (variant === 'inline') {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CodeIcon />
          <Typography variant="h6">Multi-School Prompts</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These templates control how portfolio rollups are generated. Saving creates a new
          version and makes it the active prompt for the next Generate All.
        </Typography>
        {editorBody}
      </Paper>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon />
          <Typography variant="h6">AI Prompt Editor</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {editorBody}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptEditor;

